/**
 * WildHunt WebView client entry point.
 *
 * Boots the theme, the store (which owns the connection + native bridge), and
 * the root Aberdeen view that dispatches between the lobby and the game based on
 * the live auth stream. Non-playing screens (lobby, waiting room, splash) live
 * inside a Staffa `S.main` app shell; the active game and the end screen take
 * over the full viewport with their own HUD.
 */
// Crash reporting must init before anything else so boot-time failures (and the
// store, which is constructed when imported below) are captured. This import is
// first so its module-load side effect (Sentry.init) runs before the rest.
import "./sentry";
import A from "aberdeen";
import * as S from "staffa";
import { installTheme } from "./ui/theme";
import { store } from "./store";
import { drawLobby } from "./ui/lobby";
import { drawGame } from "./ui/game";
import { drawScanSpinner } from "./ui/theme";
import { bridge } from "./native/bridge";
import { initCompass } from "./native/compass";
import { appOrigin, config } from "./config";
import type { Me, Game } from "./api-types";
import { radar, wifi, wifiOff } from "staffa/icons.js";

installTheme();

// Audio must be unlocked from a user gesture; the iOS compass permission prompt
// likewise needs one. Grab the first interaction for both.
function armAudioUnlock() {
  const once = () => {
    void store.unlockAudio();
    initCompass();
    window.removeEventListener("pointerdown", once);
    window.removeEventListener("keydown", once);
  };
  window.addEventListener("pointerdown", once);
  window.addEventListener("keydown", once);
}
armAudioUnlock();
// The Android WebView allows autoplay (mediaPlaybackRequiresUserGesture=false),
// so unlock + greet immediately rather than waiting for the first touch; stricter
// browsers stay suspended here and the gesture above takes over. unlockAudio()
// only greets once the context is actually running, so this can't waste the cue.
void store.unlockAudio();
// Also start the compass now — enough for Android/desktop, which need no permission.
initCompass();

A(() => {
  const me = store.auth.value;
  const game = me?.currentPlayer?.game;
  const phase = game?.phase;
  // During active play and on the end screen the game renders its own
  // full-bleed HUD; everything else lives in the app shell.
  const showGameScreen = !!game && (phase === "grace" || phase === "running" || phase === "ended" || store.$.leftGame);
  if (showGameScreen) {
    drawGame(me, game);
  } else {
    drawShell(me, game);
  }
});

function drawShell(me: Me | undefined, game: Game | undefined) {
  S.main({
    icon: () => radar({ size: "1.3em" }),
    title: () => drawTitle(),
    menu: () => drawLinkStatus(),
    maxWidth: "40rem",
    content: () => {
      if (me === undefined) {
        drawSplash("Establishing uplink…");
        return;
      }
      if (game) drawGame(me, game);
      else drawLobby();
    },
    footer: () => A("small letter-spacing:0.1em #WILDHUNT FIELD TERMINAL"),
  });
}

/** Live connection indicator: a green LINK pill, red OFFLINE when reconnecting. */
function drawLinkStatus() {
  A(() => {
    const online = store.$.online;
    const role = online ? ".success" : ".danger";
    A(`div.s-s${role}.tonal display:flex align-items:center gap:$1 ph:$2 pv:2px r:2px font-size:0.7rem font-weight:700 letter-spacing:0.1em`, () => {
      (online ? wifi : wifiOff)({ size: "0.85rem" });
      A(`span #${online ? "LINK" : "OFFLINE"}`);
    });
  });
}

/**
 * Triple-tapping the title toggles between the version BUNDLED in the app
 * (loaded from a `appassets…` origin) and the version served by the configured
 * backend. Detection: the two origins differ when bundled, match when
 * server-served. The shell just reloads at the URL we give it.
 */
function toggleDevVersion() {
  if (appOrigin === config.serverAssetBase) {
    bridge.showToast("Loading bundled version…");
    bridge.loadApp();
  } else {
    bridge.showToast("Loading DEV version from server…");
    bridge.loadApp(`${config.serverAssetBase}/index.html`);
  }
}

function drawTitle() {
  let taps = 0;
  let timer: ReturnType<typeof setTimeout> | undefined;
  A("span cursor:pointer #W I L D H U N T", "click=", () => {
    taps++;
    clearTimeout(timer);
    timer = setTimeout(() => {
      taps = 0;
    }, 600);
    if (taps >= 3) {
      taps = 0;
      clearTimeout(timer);
      toggleDevVersion();
    }
  });
}

function drawSplash(text: string) {
  A(
    "div flex:1 display:flex align-items:center justify-content:center flex-direction:column gap:$3 p:$3",
    () => {
      drawScanSpinner();
      A("div fg:$s-muted text-align:center letter-spacing:0.08em text=", text);
    },
  );
}
